import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { useHasStaffPermission } from '@/auth/RequireStaffPermission';
import { StaffPermissions } from '@/auth/staffPermissions';
import { formatDateTime } from '@/lib/format';
import { TenantNotFoundError, useTenant } from './tenants/useTenants';

/**
 * One tenant, as support sees it (M1-33).
 *
 * <b>Read-only, and metadata only.</b> `GET /api/v1/admin/tenants/{id}`
 * returns account metadata and the account's Owner as a contact, and nothing
 * tenant-scoped — no customers, no properties, no jobs. The endpoint's own
 * type comment says the moment one field of tenant data appears there the
 * argument is over; the same holds for this screen, which must not grow a
 * section fed by a tenant-scoped endpoint.
 *
 * ⚠️ <b>`createdAt` is rendered on the VIEWER's clock, with its time, not on
 * the tenant's.</b> That looks like a violation of the customer app's "a date
 * and its time must be read on the same clock" rule and is the opposite: the
 * rule exists because pairing `formatDate` (viewer's zone) with `formatTime`
 * (the account's) puts two different days on one row. Here both halves come
 * from one formatter, so there is one clock. It is the viewer's because
 * `TenantSummaryResponse` carries no timezone, so the list next door has no
 * choice — and a detail page that dated an account differently from the row
 * that led to it is exactly the divergence that rule is about. The tenant's
 * timezone is shown below as what it is: a fact about the tenant, not the
 * clock this screen reads.
 */
export function TenantDetail() {
  const { t } = useTranslation();
  const { tenantId } = useParams<{ tenantId: string }>();
  const tenant = useTenant(tenantId);
  const hasPermission = useHasStaffPermission();
  const canRaiseDiagnostics = hasPermission(StaffPermissions.diagnosticsAccount);

  if (tenant.isPending) {
    return <LoadingState label={t('tenantDetail.loading')} />;
  }

  if (tenant.error instanceof TenantNotFoundError) {
    // Distinct from the console's 404 screen: the address is a real route, and
    // "no tenant with that id" is a different thing to tell someone following
    // a stale link out of a ticket than "that page does not exist".
    return (
      <section className="space-y-6">
        <BackToTenants />
        <EmptyState
          title={t('tenantDetail.notFoundTitle')}
          description={t('tenantDetail.notFoundDescription')}
        />
      </section>
    );
  }

  if (tenant.isError || !tenant.data) {
    return (
      <section className="space-y-6">
        <BackToTenants />
        <ErrorState
          title={t('tenantDetail.loadErrorTitle')}
          description={t('tenantDetail.loadErrorDescription')}
          onRetry={() => void tenant.refetch()}
        />
      </section>
    );
  }

  const detail = tenant.data;
  const contact = detail.contact;

  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <BackToTenants />
          <h1 className="text-2xl font-semibold tracking-tight">{detail.name}</h1>
        </div>
        {/* M1-34's entry point. Support has just found the tenant; making them
            copy a GUID across to another screen is the step where it goes
            wrong. An account id in a query string is an identifier, which the
            telemetry rule allows — unlike M1-33's search term. */}
        {canRaiseDiagnostics ? (
          <Link
            to={`/diagnostics?account=${detail.id}`}
            className="rounded-md border px-3 py-1.5 text-sm underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
          >
            {t('tenantDetail.raiseDiagnostics')}
          </Link>
        ) : null}
      </div>

      <div className="rounded-lg border">
        <h2 className="border-b px-4 py-2 text-sm font-medium">{t('tenantDetail.accountTitle')}</h2>
        <dl className="grid gap-x-6 gap-y-3 px-4 py-4 text-sm sm:grid-cols-2">
          <Field label={t('tenantDetail.plan')}>
            {t(`tenants.plan.${detail.planTier}`, { defaultValue: detail.planTier })}
          </Field>
          <Field label={t('tenantDetail.status')}>
            {t(`tenants.status.${detail.status}`, { defaultValue: detail.status })}
          </Field>
          <Field label={t('tenantDetail.timezone')}>
            {/* Not converted to anything and not abbreviated: this is the zone
                the tenant's own jobs are scheduled on, and a support agent
                reading it out needs the IANA name they can search for. */}
            <span className="font-mono">{detail.timezone}</span>
          </Field>
          <Field label={t('tenantDetail.userCount')}>{Number(detail.userCount)}</Field>
          <Field label={t('tenantDetail.created')}>{formatDateTime(detail.createdAt)}</Field>
          <Field label={t('tenantDetail.tenantId')}>
            <span className="font-mono break-all">{detail.id}</span>
          </Field>
        </dl>
      </div>

      <div className="rounded-lg border">
        <h2 className="border-b px-4 py-2 text-sm font-medium">{t('tenantDetail.contactTitle')}</h2>
        {contact ? (
          <dl className="grid gap-x-6 gap-y-3 px-4 py-4 text-sm sm:grid-cols-2">
            <Field label={t('tenantDetail.contactName')}>{contact.name}</Field>
            <Field label={t('tenantDetail.contactStatus')}>
              {/* The owner's own status travels with them (M1-31) so an
                  Invited or Disabled owner is visible rather than presented as
                  someone who can be reached. */}
              {t(`tenantDetail.userStatus.${contact.status}`, { defaultValue: contact.status })}
            </Field>
            <Field label={t('tenantDetail.contactEmail')}>
              <a
                href={`mailto:${contact.email}`}
                className="underline underline-offset-2 focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
              >
                {contact.email}
              </a>
            </Field>
            <Field label={t('tenantDetail.contactPhone')}>
              {contact.phone ? (
                <a
                  href={`tel:${contact.phone}`}
                  className="underline underline-offset-2 focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
                >
                  {contact.phone}
                </a>
              ) : (
                <span className="text-[var(--color-muted-foreground)]">
                  {t('tenantDetail.noPhone')}
                </span>
              )}
            </Field>
          </dl>
        ) : (
          // A real answer, not a failure: an account created but never
          // provisioned has no users, and "no owner yet" is useful to whoever
          // is on the phone about exactly that.
          <p className="max-w-prose px-4 py-4 text-sm text-[var(--color-muted-foreground)]">
            {t('tenantDetail.noContact')}
          </p>
        )}
      </div>
    </section>
  );
}

function BackToTenants() {
  const { t } = useTranslation();

  return (
    <p className="text-sm">
      <Link
        to="/tenants"
        className="text-[var(--color-muted-foreground)] underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
      >
        {t('tenantDetail.backToTenants')}
      </Link>
    </p>
  );
}

/** One `<dt>`/`<dd>` pair, so the grid keeps them adjacent in source order. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-[var(--color-muted-foreground)]">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}
